import { FC, useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { FormControl } from '@mui/material'

import { SelectMenu, SelectMenuProps } from '../../../../../components/SelectMenu/SelectMenu'
import { TrackedGameArchetype } from '../../../../../features/live-events/types/TrackedGame'
import { useLiveEventsTrackingUserLanguage } from '../../hooks/useLiveEventsTrackingUserLanguage'

/**
 * Props for the EventArchetypeSelectMenu component.
 * Extends SelectMenuProps but omits 'label' and 'options' as they are handled internally.
 */
type EventArchetypeSelectMenuProps = Omit<SelectMenuProps<string>, 'label' | 'options'> & {
  /** The currently selected archetype ID */
  value: string
  /** Callback function called when an archetype is selected */
  onChange: (archetypeId: string) => void
  /** Array of available game archetypes to display in the dropdown */
  archetypes: TrackedGameArchetype[]
}

/**
 * A select menu component for choosing game archetypes in live events tracking.
 *
 * This component renders a dropdown menu that displays available game archetypes
 * sorted alphabetically by name. The archetype names are displayed in the user's
 * preferred language, falling back to English if the translation is not available.
 * Includes a "None" option to clear the selection.
 *
 * @param props - The component props
 * @param props.value - The currently selected archetype ID
 * @param props.onChange - Callback function called when an archetype is selected
 * @param props.archetypes - Array of available game archetypes
 * @param props.selectMenuProps - Additional props to pass to the underlying SelectMenu component
 *
 * @returns A FormControl wrapped SelectMenu component for archetype selection
 */
export const EventArchetypeSelectMenu: FC<EventArchetypeSelectMenuProps> = ({ value, onChange, archetypes, ...selectMenuProps }) => {
  const userLanguage = useLiveEventsTrackingUserLanguage()
  const { t } = useTranslation()

  /**
   * Memoized options for the select menu, derived from the archetypes array.
   * Each option contains the archetype ID as value and the localized name as display text.
   * Options are sorted alphabetically by name.
   * Includes a "None" option at the beginning to allow clearing the selection.
   */
  const archetypeOptions = useMemo(() => {
    const archetypeOptionsList =
      archetypes
        .map((archetype) => ({
          value: archetype.archetypeId,
          name: archetype.names[userLanguage] || archetype.names.en || archetype.archetypeId,
        }))
        .sort((a, b) => a.name?.localeCompare(b.name)) || []

    // Add "None" option at the beginning
    return [{ value: '', name: t('internal-common:none') }, ...archetypeOptionsList]
  }, [archetypes, t, userLanguage])

  return (
    <FormControl size="small" variant="outlined" fullWidth>
      <SelectMenu
        {...selectMenuProps}
        options={archetypeOptions}
        onChange={(e) => onChange(e.target.value)}
        value={value}
        label={<Trans i18nKey="internal-live-events:event_archetype" />}
      />
    </FormControl>
  )
}
